import type { StructuredHeader } from './header';

// Constraint for items that can have their weight calculated
type WeightableItem = {
  links?: unknown[];
  [key: string]: unknown;
};

/**
 * Calculate the visual weight of a link group
 * Weight = 1 (for the main link) + number of sub-links
 */
const calculateLinkWeight = <T extends WeightableItem>(
  linkGroup: T,
): number => {
  if (!linkGroup) return 1;

  const subLinkCount = Array.isArray(linkGroup.links)
    ? linkGroup.links.length
    : 0;
  return 1 + subLinkCount;
};

/**
 * Determines how many items should go in each column for balanced distribution
 */
const calculateColumnDistribution = <T extends WeightableItem>(
  items: T[],
  columnCount: number,
): number[] => {
  const totalWeight = items.reduce(
    (sum, item) => sum + calculateLinkWeight(item),
    0,
  );
  const targetWeightPerColumn = totalWeight / columnCount;

  const distribution: number[] = [];
  let currentIndex = 0;

  // If we have fewer items than columns, use simple round-robin distribution
  if (items.length <= columnCount) {
    for (let col = 0; col < columnCount; col++) {
      if (currentIndex < items.length) {
        distribution.push(1);
        currentIndex++;
      } else {
        distribution.push(0);
      }
    }
    return distribution;
  }

  for (let col = 0; col < columnCount; col++) {
    let columnWeight = 0;
    let itemsInColumn = 0;

    // Fill this column until we reach target weight or run out of items
    while (
      currentIndex < items.length &&
      columnWeight < targetWeightPerColumn
    ) {
      const itemWeight = calculateLinkWeight(items[currentIndex]);

      // If adding this item would exceed 150% of target and we have more columns, stop
      if (
        columnWeight + itemWeight > targetWeightPerColumn * 1.5 &&
        col < columnCount - 1
      ) {
        break;
      }

      columnWeight += itemWeight;
      itemsInColumn++;
      currentIndex++;
    }

    // If we're on the last column, take all remaining items
    if (col === columnCount - 1) {
      itemsInColumn += items.length - currentIndex;
    }

    distribution.push(itemsInColumn);
  }

  return distribution;
};

/**
 * Arranges links into columns
 */
const arrangeLinksIntoColumns = <T extends WeightableItem>(
  navLinks: T[] | undefined | null,
  columnCount: number = 4,
  firstColumnMaxSize?: number | null,
): T[][] => {
  if (!navLinks || navLinks.length === 0) {
    return Array.from({ length: columnCount }, () => []);
  }

  // Handle special case where firstColumnMaxSize is 0 (skip first column)
  if (firstColumnMaxSize === 0) {
    const columns: T[][] = [[]]; // Empty first column
    const remainingColumnCount = columnCount - 1;

    if (remainingColumnCount <= 0) {
      return columns;
    }

    // Distribute all items across remaining columns
    const distribution = calculateColumnDistribution(
      navLinks,
      remainingColumnCount,
    );
    let itemIndex = 0;

    for (let col = 0; col < remainingColumnCount; col++) {
      const columnItems: T[] = [];
      const itemsForThisColumn = distribution[col];

      for (
        let i = 0;
        i < itemsForThisColumn && itemIndex < navLinks.length;
        i++
      ) {
        columnItems.push(navLinks[itemIndex]);
        itemIndex++;
      }

      columns.push(columnItems);
    }

    return columns;
  }

  // Handle case with specified first column size
  if (firstColumnMaxSize != null) {
    const firstColSize = Math.min(firstColumnMaxSize, navLinks.length);
    const firstColItems = navLinks.slice(0, firstColSize);
    const remainingItems = navLinks.slice(firstColSize);

    const columns: T[][] = [firstColItems];

    if (remainingItems.length === 0) {
      // All items fit in first column, fill remaining columns with empty arrays
      for (let i = 1; i < columnCount; i++) {
        columns.push([]);
      }
      return columns;
    }

    // Distribute remaining items across remaining columns
    const remainingColumnCount = columnCount - 1;
    const distribution = calculateColumnDistribution(
      remainingItems,
      remainingColumnCount,
    );
    let itemIndex = 0;

    for (let col = 0; col < remainingColumnCount; col++) {
      const columnItems: T[] = [];
      const itemsForThisColumn = distribution[col];

      for (
        let i = 0;
        i < itemsForThisColumn && itemIndex < remainingItems.length;
        i++
      ) {
        columnItems.push(remainingItems[itemIndex]);
        itemIndex++;
      }

      columns.push(columnItems);
    }

    return columns;
  }

  // Default case: distribute all items across all columns
  const distribution = calculateColumnDistribution(navLinks, columnCount);
  const columns: T[][] = [];
  let itemIndex = 0;

  for (let col = 0; col < columnCount; col++) {
    const columnItems: T[] = [];
    const itemsForThisColumn = distribution[col];

    // Fill this column from top to bottom
    for (
      let i = 0;
      i < itemsForThisColumn && itemIndex < navLinks.length;
      i++
    ) {
      columnItems.push(navLinks[itemIndex]);
      itemIndex++;
    }

    columns.push(columnItems);
  }

  return columns;
};

export const arrangeNavLinks = (navLinks: StructuredHeader['navLinks']) => {
  return navLinks?.map((navLink) => {
    const filteredLinkGroup = (navLink.linkGroup || []).filter(
      (group): group is NonNullable<typeof group> => group !== null,
    );

    const arrangedGroups = arrangeLinksIntoColumns(
      filteredLinkGroup,
      4,
      navLink?.firstColumnCount,
    );

    return {
      ...navLink,
      linkGroup: arrangedGroups,
    };
  });
};
